import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { useAuthStore } from '@/auth/auth-store';

/**
 * Referrals whose appointment was booked but whose "mark booked" call failed (PROD-1395):
 * referralId -> appointmentId. The Referrals tab offers "Mark booked" for these rows instead of
 * "Book appointment", so a second appointment is never made. Kept in sessionStorage so it survives a
 * tab switch and a reload (not a new browser session). Ids only, scoped to the signed-in user.
 * A storage that throws (private window, blocked site data) falls back to memory.
 */
const memory = new Map<string, string>();

const suffix = () => useAuthStore.getState().user?.userId ?? 'anon';

export const safeSessionStorage: StateStorage = {
  getItem: (name) => {
    const key = `${name}.${suffix()}`;
    try {
      return window.sessionStorage.getItem(key) ?? memory.get(key) ?? null;
    } catch {
      return memory.get(key) ?? null;
    }
  },
  setItem: (name, value) => {
    const key = `${name}.${suffix()}`;
    memory.set(key, value);
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // memory copy above is the fallback
    }
  },
  removeItem: (name) => {
    const key = `${name}.${suffix()}`;
    memory.delete(key);
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // nothing stored
    }
  },
};

interface ReferralsUnmarkedState {
  unmarked: Record<string, string>;
  remember: (referralId: string, appointmentId: string) => void;
  forget: (referralId: string) => void;
}

export const useReferralsUnmarkedStore = create<ReferralsUnmarkedState>()(
  persist(
    (set) => ({
      unmarked: {},
      remember: (referralId, appointmentId) =>
        set((s) => ({ unmarked: { ...s.unmarked, [referralId]: appointmentId } })),
      forget: (referralId) =>
        set((s) => ({
          unmarked: Object.fromEntries(Object.entries(s.unmarked).filter(([id]) => id !== referralId)),
        })),
    }),
    {
      name: 'scrips.referrals.unmarked',
      storage: createJSONStorage(() => safeSessionStorage),
      partialize: (s) => ({ unmarked: s.unmarked }) as ReferralsUnmarkedState,
      // Replace, never merge: what this user stored is the whole truth, so a user switch cannot carry the
      // earlier user's ids over (nothing stored reads as empty).
      merge: (persisted, current) => ({
        ...current,
        unmarked: (persisted as Partial<ReferralsUnmarkedState> | undefined)?.unmarked ?? {},
      }),
    },
  ),
);

// The storage key is per user. When the signed-in user changes (the user loads after a reload, or another
// user signs in), read that user's key.
if (typeof window !== 'undefined') {
  let lastUserId = useAuthStore.getState().user?.userId;
  useAuthStore.subscribe((state) => {
    const id = state.user?.userId;
    if (id === lastUserId) return;
    lastUserId = id;
    void useReferralsUnmarkedStore.persist.rehydrate();
  });
}
